import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { isMarkdownString, MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { ICodeEditorService } from 'vscode/vscode/vs/editor/browser/services/codeEditorService';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { EditorPaneDescriptor } from 'vscode/vscode/vs/workbench/browser/editor';
import { Extensions as Extensions$1, registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { EditorExtensions } from 'vscode/vscode/vs/workbench/common/editor';
import { IAccessibleViewService } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleView.service';
import { AccessibleViewAction } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleViewActions';
import { alertFocusChange } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleViewContributions';
import { registerChatActions } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatActions';
import { ACTION_ID_NEW_CHAT, registerNewChatActions } from './actions/chatClearActions.js';
import { registerChatCodeBlockActions, registerChatCodeCompareBlockActions } from './actions/chatCodeblockActions.js';
import { registerChatCopyActions } from './actions/chatCopyActions.js';
import { SubmitAction, registerChatExecuteActions } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatExecuteActions';
import { registerChatFileTreeActions } from './actions/chatFileTreeActions.js';
import { registerChatExportActions } from './actions/chatImportExport.js';
import { registerMoveActions } from './actions/chatMoveActions.js';
import { registerQuickChatActions } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatQuickInputActions';
import { registerChatTitleActions } from './actions/chatTitleActions.js';
import { IChatWidgetService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import './chatAccessibilityService.js';
import { ChatEditor } from './chatEditor.js';
import { ChatEditorInput, ChatEditorInputSerializer } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chatEditorInput';
import './chatQuick.js';
import './chatVariables.js';
import 'vscode/vscode/vs/workbench/contrib/chat/browser/chatWidget';
import './contrib/chatHistoryVariables.js';
import './contrib/chatInputEditorContrib.js';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatAgentService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents.service';
import { CONTEXT_IN_CHAT_SESSION } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatContextKeys';
import { ChatWelcomeMessageModel } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatModel';
import { chatAgentLeader, chatSubcommandLeader, chatVariableLeader } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatParserTypes';
import '../common/chatServiceImpl.js';
import '../common/chatSlashCommands.js';
import { IChatSlashCommandService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatSlashCommands.service';
import { IChatVariablesService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatVariables.service';
import { isResponseVM } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatViewModel';
import '../common/chatWidgetHistoryService.js';
import 'vscode/vscode/vs/base/common/event';
import '../common/languageModelStats.js';
import '../common/voiceChat.js';
import { RegisteredEditorPriority } from 'vscode/vscode/vs/workbench/services/editor/common/editorResolverService';
import { IEditorResolverService } from 'vscode/vscode/vs/workbench/services/editor/common/editorResolverService.service';
import 'vscode/vscode/vs/workbench/contrib/chat/common/chatColors';
import { ChatExtensionPointHandler } from './chatParticipantContributions.js';

const _moduleId = "vs/workbench/contrib/chat/browser/chat.contribution";
const configurationRegistry = ( (Registry.as(Extensions.Configuration)));
configurationRegistry.registerConfiguration({
    id: 'chatSidebar',
    title: ( localizeWithPath(_moduleId, 0, "Chat")),
    type: 'object',
    properties: {
        'chat.editor.fontSize': {
            type: 'number',
            description: ( localizeWithPath(_moduleId, 1, "Controls the font size in pixels in chat codeblocks.")),
            default: isMacintosh ? 12 : 14,
        },
        'chat.editor.fontFamily': {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 2, "Controls the font family in chat codeblocks.")),
            default: 'default'
        },
        'chat.editor.fontWeight': {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 3, "Controls the font weight in chat codeblocks.")),
            default: 'default'
        },
        'chat.editor.wordWrap': {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 4, "Controls whether lines should wrap in chat codeblocks.")),
            default: 'off',
            enum: ['on', 'off']
        },
        'chat.editor.lineHeight': {
            type: 'number',
            description: ( localizeWithPath(
                _moduleId,
                5,
                "Controls the line height in pixels in chat codeblocks. Use 0 to compute the line height from the font size."
            )),
            default: 0
        },
        'chat.experimental.implicitContext': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                6,
                "Controls whether a checkbox is shown to allow the user to determine which implicit context is included with a chat participant's prompt."
            )),
            default: false
        },
    }
});
( (Registry.as(EditorExtensions.EditorPane))).registerEditorPane(EditorPaneDescriptor.create(ChatEditor, ChatEditorInput.EditorID, ( localizeWithPath(_moduleId, 7, "Chat"))), [
    ( (new SyncDescriptor(ChatEditorInput)))
]);
let ChatResolverContribution = class ChatResolverContribution extends Disposable {
    static { this.ID = 'workbench.contrib.chatResolver'; }
    constructor(editorResolverService, instantiationService) {
        super();
        this._register(editorResolverService.registerEditor(`${Schemas.vscodeChatSesssion}:**/**`, {
            id: ChatEditorInput.EditorID,
            label: ( localizeWithPath(_moduleId, 7, "Chat")),
            priority: RegisteredEditorPriority.builtin
        }, {
            singlePerResource: true,
            canSupportResource: resource => resource.scheme === Schemas.vscodeChatSesssion
        }, {
            createEditorInput: ({ resource, options }) => {
                return { editor: instantiationService.createInstance(ChatEditorInput, resource, options), options };
            }
        }));
    }
};
ChatResolverContribution = ( (__decorate([
    ( (__param(0, IEditorResolverService))),
    ( (__param(1, IInstantiationService)))
], ChatResolverContribution)));
class ChatAccessibleViewContribution extends Disposable {
    constructor() {
        super();
        this._register(AccessibleViewAction.addImplementation(100, 'panelChat', accessor => {
            const accessibleViewService = accessor.get(IAccessibleViewService);
            const widgetService = accessor.get(IChatWidgetService);
            const codeEditorService = accessor.get(ICodeEditorService);
            return renderAccessibleView(accessibleViewService, widgetService, codeEditorService, true);
            function renderAccessibleView(accessibleViewService, widgetService, codeEditorService, initialRender) {
                const widget = widgetService.lastFocusedWidget;
                if (!widget) {
                    return false;
                }
                const chatInputFocused = initialRender && !!codeEditorService.getFocusedCodeEditor();
                if (initialRender && chatInputFocused) {
                    widget.focusLastMessage();
                }
                if (!widget) {
                    return false;
                }
                const verifiedWidget = widget;
                const focusedItem = verifiedWidget.getFocus();
                if (!focusedItem) {
                    return false;
                }
                widget.focus(focusedItem);
                const isWelcome = focusedItem instanceof ChatWelcomeMessageModel;
                let responseContent = isResponseVM(focusedItem) ? focusedItem.response.asString() : undefined;
                if (isWelcome) {
                    const welcomeReplyContents = [];
                    for (const content of focusedItem.content) {
                        if (Array.isArray(content)) {
                            welcomeReplyContents.push(...( (content.map(m => m.message))));
                        }
                        else {
                            welcomeReplyContents.push(content.value);
                        }
                    }
                    responseContent = welcomeReplyContents.join('\n');
                }
                if (!responseContent && 'errorDetails' in focusedItem && focusedItem.errorDetails) {
                    responseContent = focusedItem.errorDetails.message;
                }
                if (!responseContent) {
                    return false;
                }
                const responses = verifiedWidget.viewModel?.getItems().filter(i => isResponseVM(i));
                const length = responses?.length;
                const responseIndex = responses?.findIndex(i => i === focusedItem);
                accessibleViewService.show({
                    id: "panelChat" ,
                    verbositySettingKey: "accessibility.verbosity.panelChat" ,
                    provideContent() { return responseContent; },
                    onClose() {
                        verifiedWidget.reveal(focusedItem);
                        if (chatInputFocused) {
                            verifiedWidget.focusInput();
                        }
                        else {
                            verifiedWidget.focus(focusedItem);
                        }
                    },
                    next() {
                        verifiedWidget.moveFocus(focusedItem, 'next');
                        alertFocusChange(responseIndex, length, 'next');
                        renderAccessibleView(accessibleViewService, widgetService, codeEditorService);
                    },
                    previous() {
                        verifiedWidget.moveFocus(focusedItem, 'previous');
                        alertFocusChange(responseIndex, length, 'previous');
                        renderAccessibleView(accessibleViewService, widgetService, codeEditorService);
                    },
                    options: { type: "view"  }
                });
                return true;
            }
        }, CONTEXT_IN_CHAT_SESSION));
    }
}
let ChatSlashStaticSlashCommandsContribution = class ChatSlashStaticSlashCommandsContribution extends Disposable {
    constructor(slashCommandService, commandService, chatAgentService, chatVariablesService) {
        super();
        this._store.add(slashCommandService.registerSlashCommand({
            command: 'clear',
            detail: ( localizeWithPath(_moduleId, 8, "Start a new chat")),
            sortText: 'z2_clear',
            executeImmediately: true
        }, async () => {
            commandService.executeCommand(ACTION_ID_NEW_CHAT);
        }));
        this._store.add(slashCommandService.registerSlashCommand({
            command: 'help',
            detail: '',
            sortText: 'z1_help',
            executeImmediately: true
        }, async (prompt, progress) => {
            const defaultAgent = chatAgentService.getDefaultAgent(ChatAgentLocation.Panel);
            const agents = chatAgentService.getAgents();
            if (defaultAgent?.metadata.helpTextPrefix) {
                if (isMarkdownString(defaultAgent.metadata.helpTextPrefix)) {
                    progress.report({ content: defaultAgent.metadata.helpTextPrefix, kind: 'markdownContent' });
                }
                else {
                    progress.report({ content: ( (new MarkdownString(defaultAgent.metadata.helpTextPrefix))), kind: 'markdownContent' });
                }
                progress.report({ content: ( (new MarkdownString('\n\n'))), kind: 'markdownContent' });
            }
            const agentText = (await Promise.all(( (agents
                .filter(a => a.id !== defaultAgent?.id)
                .filter(a => a.locations.includes(ChatAgentLocation.Panel))
                .map(async (a) => {
                const agentWithLeader = `${chatAgentLeader}${a.name}`;
                const actionArg = { inputValue: `${agentWithLeader} ${a.metadata.sampleRequest}` };
                const urlSafeArg = encodeURIComponent(JSON.stringify(actionArg));
                const description = a.description ? `- ${a.description}` : '';
                const agentLine = `* [\`${agentWithLeader}\`](command:${SubmitAction.ID}?${urlSafeArg}) ${description}`;
                const commandText = ( (a.slashCommands.map(c => {
                    const actionArg = { inputValue: `${agentWithLeader} ${chatSubcommandLeader}${c.name} ${c.sampleRequest ?? ''}` };
                    const urlSafeArg = encodeURIComponent(JSON.stringify(actionArg));
                    const description = c.description ? `- ${c.description}` : '';
                    return `\t* [\`${chatSubcommandLeader}${c.name}\`](command:${SubmitAction.ID}?${urlSafeArg}) ${description}`;
                }))).join('\n');
                return (agentLine + '\n' + commandText).trim();
            }))))).join('\n');
            progress.report({ content: ( (new MarkdownString(agentText, { isTrusted: { enabledCommands: [SubmitAction.ID] } }))), kind: 'markdownContent' });
            if (defaultAgent?.metadata.helpTextVariablesPrefix) {
                progress.report({ content: ( (new MarkdownString('\n\n'))), kind: 'markdownContent' });
                if (isMarkdownString(defaultAgent.metadata.helpTextVariablesPrefix)) {
                    progress.report({ content: defaultAgent.metadata.helpTextVariablesPrefix, kind: 'markdownContent' });
                }
                else {
                    progress.report({ content: ( (new MarkdownString(defaultAgent.metadata.helpTextVariablesPrefix))), kind: 'markdownContent' });
                }
                const variables = [
                    ...chatVariablesService.getVariables(),
                    { name: 'file', description: ( localizeWithPath(_moduleId, 9, "Choose a file in the workspace")) }
                ];
                const variableText = ( (variables
                    .map(v => `* \`${chatVariableLeader}${v.name}\` - ${v.description}`)))
                    .join('\n');
                progress.report({ content: ( (new MarkdownString('\n' + variableText))), kind: 'markdownContent' });
            }
            if (defaultAgent?.metadata.helpTextPostfix) {
                progress.report({ content: ( (new MarkdownString('\n\n'))), kind: 'markdownContent' });
                if (isMarkdownString(defaultAgent.metadata.helpTextPostfix)) {
                    progress.report({ content: defaultAgent.metadata.helpTextPostfix, kind: 'markdownContent' });
                }
                else {
                    progress.report({ content: ( (new MarkdownString(defaultAgent.metadata.helpTextPostfix))), kind: 'markdownContent' });
                }
            }
        }));
    }
};
ChatSlashStaticSlashCommandsContribution = ( (__decorate([
    ( (__param(0, IChatSlashCommandService))),
    ( (__param(1, ICommandService))),
    ( (__param(2, IChatAgentService))),
    ( (__param(3, IChatVariablesService)))
], ChatSlashStaticSlashCommandsContribution)));
const workbenchContributionsRegistry = ( (Registry.as(Extensions$1.Workbench)));
registerWorkbenchContribution2(ChatResolverContribution.ID, ChatResolverContribution, 1 );
workbenchContributionsRegistry.registerWorkbenchContribution(ChatAccessibleViewContribution, 4 );
workbenchContributionsRegistry.registerWorkbenchContribution(ChatSlashStaticSlashCommandsContribution, 4 );
( (Registry.as(EditorExtensions.EditorFactory))).registerEditorSerializer(ChatEditorInput.TypeID, ChatEditorInputSerializer);
registerWorkbenchContribution2(ChatExtensionPointHandler.ID, ChatExtensionPointHandler, 1 );
registerChatActions();
registerChatCopyActions();
registerChatCodeBlockActions();
registerChatCodeCompareBlockActions();
registerChatFileTreeActions();
registerChatTitleActions();
registerChatExecuteActions();
registerQuickChatActions();
registerChatExportActions();
registerMoveActions();
registerNewChatActions();
